import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, MessageCircle, ArrowLeft } from "lucide-react";
import { useCart } from "../context/CartContext";
import { api, formatApiError, formatPrice } from "../lib/api";
import { SITE } from "../constants/site";
import { buildWhatsAppMessage } from "../components/CartDrawer";

const INIT = { name: "", phone: "", email: "", address: "", city: "Dakar" };

export default function Checkout() {
  const { items, total, clear } = useCart();
  const [form, setForm] = useState(INIT);
  const [method, setMethod] = useState("whatsapp");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    setBusy(true);
    try {
      const { data: order } = await api.post("/orders", { items: items.map((i) => ({ product_id: i.id, quantity: i.qty })), customer: form, method });
      if (method === "stripe") {
        const { data } = await api.post("/payments/checkout", { order_id: order.id, origin_url: window.location.origin });
        window.location.href = data.checkout_url;
        return;
      }
      window.open(`https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent(buildWhatsAppMessage(items, total, order.id))}`, "_blank");
      clear();
      navigate(`/commande/confirmation?order=${order.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy(false);
    }
  };

  return (
    <main data-testid="checkout-page" className="min-h-screen bg-ice pt-28 pb-20">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 sm:px-8 lg:grid-cols-12 lg:px-16">
        <div className="lg:col-span-7">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-navy"><ArrowLeft className="h-4 w-4" /> Retour à la boutique</Link>
          <p className="eyebrow mt-6 text-royal">Commande</p>
          <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight text-navy sm:text-5xl">Finaliser votre commande</h1>

          {items.length === 0 ? (
            <p data-testid="checkout-empty" className="mt-10 text-slate-500">Votre panier est vide. <Link to="/" className="text-royal underline">Voir le catalogue</Link></p>
          ) : (
            <form onSubmit={submit} data-testid="checkout-form" className="mt-10 grid gap-6 sm:grid-cols-2">
              <input data-testid="checkout-name-input" required value={form.name} onChange={set("name")} placeholder="Nom complet *" className="field" />
              <input data-testid="checkout-phone-input" required value={form.phone} onChange={set("phone")} placeholder="Téléphone *" className="field" />
              <input data-testid="checkout-email-input" type="email" value={form.email} onChange={set("email")} placeholder="Email" className="field" />
              <input data-testid="checkout-city-input" required value={form.city} onChange={set("city")} placeholder="Ville *" className="field" />
              <input data-testid="checkout-address-input" required value={form.address} onChange={set("address")} placeholder="Adresse de livraison *" className="field sm:col-span-2" />

              <div className="sm:col-span-2 mt-4">
                <p className="eyebrow text-royal">Mode de commande</p>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  {[
                    { k: "whatsapp", icon: MessageCircle, t: "Commander par WhatsApp", d: "Confirmation et paiement à la livraison ou par mobile money." },
                    { k: "stripe", icon: CreditCard, t: "Carte bancaire", d: "Paiement sécurisé en ligne via Stripe." },
                  ].map((m) => (
                    <button type="button" key={m.k} data-testid={`method-${m.k}`} onClick={() => setMethod(m.k)} className={`flex items-start gap-4 border p-5 text-left transition-colors duration-300 ${method === m.k ? "border-royal bg-white" : "border-navy/10 bg-white/50 hover:border-navy/30"}`}>
                      <m.icon className={`mt-0.5 h-5 w-5 ${method === m.k ? "text-royal" : "text-slate-400"}`} />
                      <div><p className="font-display font-semibold text-navy">{m.t}</p><p className="mt-1 text-xs text-slate-500">{m.d}</p></div>
                    </button>
                  ))}
                </div>
              </div>

              <button data-testid="checkout-submit-button" disabled={busy} className={`btn-pill justify-center sm:col-span-2 disabled:opacity-60 ${method === "stripe" ? "bg-royal text-white hover:bg-navy" : "bg-wa text-white hover:bg-[#1ebe5b]"}`}>
                {busy ? "Traitement…" : method === "stripe" ? `Payer ${formatPrice(total)}` : "Envoyer la commande sur WhatsApp"}
              </button>
            </form>
          )}
        </div>

        <aside className="lg:col-span-5">
          <div data-testid="checkout-summary" className="border border-navy/10 bg-white p-8 lg:sticky lg:top-28">
            <p className="eyebrow text-royal">Récapitulatif</p>
            <ul className="mt-6 divide-y divide-navy/10">
              {items.map((i) => (
                <li key={i.id} className="flex items-center justify-between gap-4 py-3 text-sm">
                  <span className="text-navy">{i.name} <span className="text-slate-400">× {i.qty}</span></span>
                  <span className="font-semibold text-navy">{formatPrice(i.price * i.qty)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 flex items-center justify-between border-t border-navy/10 pt-6">
              <span className="text-slate-500">Total</span>
              <span data-testid="checkout-total" className="font-display text-3xl font-bold text-navy">{formatPrice(total)}</span>
            </div>
          </div>
        </aside>
      </div>
    </main>
  );
}
